import { useAnimationControls } from "motion/react";
import { useEffect } from "react";

const abortableTimeout = (ms: number, signal: AbortSignal) => {
    return new Promise<void>((resolve) => {
        const timeoutId = setTimeout(() => resolve(), ms);
        signal.addEventListener("abort", () => {
            clearTimeout(timeoutId);
        });
    });
};

async function startAtExactSecond(callback: () => Promise<void> | void) {
    const now = new Date();
    const milliseconds = now.getMilliseconds();
    const delay = 1000 - milliseconds;

    await new Promise<void>((resolve, reject) =>
        setTimeout(() => {
            callback()?.then(resolve).catch(reject);
        }, delay)
    );
}

const makePulseAnimation = (controls: ReturnType<typeof useAnimationControls>, color: string, signal: AbortSignal) => () => {
    signal.addEventListener("abort", () => {
        controls.stop();
    });

    if (!signal.aborted) {
        return controls.start({
            boxShadow: [`0 0 0px ${color}`, `0 0 8px ${color}`, `0 0 0px ${color}`],
            transition: { duration: 0.3, ease: "easeInOut" },
        });
    }
};

export function usePulsingBoxShadow(color: string) {
    const controls = useAnimationControls();

    useEffect(() => {
        const abortController = new AbortController();
        const pulseAnimation = makePulseAnimation(controls, color, abortController.signal);

        async function runSequence() {
            const doublePulse = async () => {
                await startAtExactSecond(pulseAnimation);
                await abortableTimeout(300, abortController.signal);
                await startAtExactSecond(pulseAnimation);
            };

            for (let i = 0; i < 3; i++) {
                await doublePulse();
                await abortableTimeout(3700, abortController.signal);
            }

            if (!abortController.signal.aborted) {
                await controls.start({
                    boxShadow: "none",
                    transition: { duration: 0.3 },
                });
            }
        }

        void runSequence();

        return () => abortController.abort();
    }, [controls, color]);

    const initial = { boxShadow: "none" };

    return { initial, controls };
}
